import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

export default function Navbar() {
  const { user, logout } = useAuth();
  const { isDark, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const isActive = path =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  useEffect(() => {
    const handler = e => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = () => { setMenuOpen(false); logout(); navigate('/'); };

  const initials = u => {
    if (!u) return '?';
    if (u.first_name) return (u.first_name[0] + (u.last_name?.[0] || '')).toUpperCase();
    return u.username[0].toUpperCase();
  };

  const menuItems = user ? [
    { to: `/profile/${user.username}`, icon: '👤', label: 'My Profile' },
    { to: '/write', icon: '✏️', label: 'Write Story' },
    { to: `/profile/${user.username}?tab=drafts`, icon: '📝', label: 'My Drafts' },
    { to: `/profile/${user.username}?tab=bookmarks`, icon: '🔖', label: 'Bookmarks' },
  ] : [];

  return (
    <nav className="navbar">
      <div className="navbar-inner">
        {/* Logo */}
        <Link to="/" className="nav-logo">
          <span className="nav-logo-mark">P</span>
          Pitch &amp; Pen
        </Link>

        {/* Center nav links */}
        <div className="nav-links">
          <Link to="/stories" className={`nav-link${isActive('/stories') ? ' active' : ''}`}>
            Stories
          </Link>
          <Link to="/players" className={`nav-link${isActive('/players') ? ' active' : ''}`}>
            Players
          </Link>
          {user && (
            <Link to="/write" className={`nav-link${isActive('/write') ? ' active' : ''}`}>
              Write
            </Link>
          )}
        </div>

        {/* Right actions */}
        <div className="nav-actions">
          {/* Dark/Light toggle */}
          <button
            className="theme-toggle"
            onClick={toggleTheme}
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-label="Toggle theme"
          >
            {isDark ? '☀️' : '🌙'}
          </button>

          {user ? (
            <div style={{ position: 'relative' }} ref={menuRef}>
              <button
                onClick={() => setMenuOpen(o => !o)}
                style={{ background: 'none', border: 'none', padding: 0 }}
                aria-label="User menu"
              >
                {user.avatar
                  ? <img src={user.avatar} alt={user.username} className="nav-avatar" />
                  : <div className="nav-avatar-fallback">{initials(user)}</div>
                }
              </button>

              {menuOpen && (
                <div className="nav-dropdown">
                  {/* User info header */}
                  <div style={{
                    padding: '0.7rem 0.85rem 0.6rem',
                    borderBottom: '1px solid var(--border)',
                    marginBottom: '0.3rem',
                  }}>
                    <div style={{
                      fontFamily: 'var(--font-sans)', fontWeight: 700,
                      fontSize: '0.875rem', color: 'var(--text)',
                    }}>
                      {user.first_name ? `${user.first_name} ${user.last_name}` : user.username}
                    </div>
                    <div style={{
                      fontFamily: 'var(--font-sans)', fontSize: '0.75rem',
                      color: 'var(--text-light)', marginTop: '1px',
                    }}>
                      @{user.username} · {user.role}
                    </div>
                  </div>

                  {menuItems.map(item => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="nav-dropdown-item"
                      onClick={() => setMenuOpen(false)}
                    >
                      <span style={{ fontSize: '0.9rem', width: 18, textAlign: 'center' }}>{item.icon}</span>
                      {item.label}
                    </Link>
                  ))}

                  <div style={{ borderTop: '1px solid var(--border)', marginTop: '0.3rem', paddingTop: '0.3rem' }}>
                    <button className="nav-dropdown-item danger" onClick={handleLogout}>
                      <span style={{ fontSize: '0.9rem', width: 18, textAlign: 'center' }}>↩</span>
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link to="/login" className="btn btn-ghost btn-sm">Sign in</Link>
              <Link to="/register" className="btn btn-primary btn-sm">Join free</Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
